import { Controller } from "@hotwired/stimulus"

// Friendly UI on top of the raw cron_expression textbox.
//
// Operators pick a Frequency (Daily / Weekly / Custom), a Time, and —
// for Weekly — a set of weekdays. The controller assembles a canonical
// 5-field cron string and writes it to the hidden cron_expression
// input. The advanced toggle reveals the raw textbox for power users
// who need something the structured fields can't express.
//
// Times are interpreted in UTC. We don't try to localize because
// (a) good_job's cron parser interprets in the worker process's TZ,
// (b) the worker runs UTC in production, (c) showing "2:00 UTC = X:XX
// local" adds clutter on a form most operators see once or twice.
export default class extends Controller {
  static targets = [
    "frequency", "time",
    "weekdayWrapper", "weekday",
    "advancedToggle", "advanced",
    "structured",
    "cronHidden", "cronPreview",
  ]

  connect() {
    this.hydrateFromCron(this.cronHiddenTarget.value)
    this.refresh()
  }

  // Parse the current cron value into the structured fields if it
  // matches a preset; otherwise force the advanced toggle on and stash
  // the raw value in the textbox.
  hydrateFromCron(cron) {
    if (!cron) {
      this.frequencyTarget.value = "daily"
      this.timeTarget.value      = "02:00"
      this.setAdvanced(false)
      return
    }

    const daily = cron.match(/^(\d{1,2}) (\d{1,2}) \* \* \*$/)
    if (daily) {
      this.frequencyTarget.value = "daily"
      this.timeTarget.value      = this.hhmm(daily[2], daily[1])
      this.setAdvanced(false)
      return
    }

    const weekly = cron.match(/^(\d{1,2}) (\d{1,2}) \* \* ([0-6](?:,[0-6])*)$/)
    if (weekly) {
      this.frequencyTarget.value = "weekly"
      this.timeTarget.value      = this.hhmm(weekly[2], weekly[1])
      const days = new Set(weekly[3].split(",").map(d => parseInt(d, 10)))
      for (const el of this.weekdayTargets) {
        el.checked = days.has(parseInt(el.value, 10))
      }
      this.setAdvanced(false)
      return
    }

    // Doesn't fit a preset — flip to advanced mode.
    this.advancedTarget.value = cron
    this.setAdvanced(true)
  }

  // Live-update the hidden cron field whenever any control changes.
  refresh() {
    const isAdvanced = this.advancedToggleTarget.checked
    const cron = isAdvanced ? this.advancedTarget.value : this.composeCron()
    this.cronHiddenTarget.value = cron
    this.cronPreviewTarget.textContent = cron || "—"

    // Show / hide weekday checkboxes based on frequency.
    if (this.hasWeekdayWrapperTarget) {
      this.weekdayWrapperTarget.hidden = isAdvanced || this.frequencyTarget.value !== "weekly"
    }
    // Show / hide the structured-fields container based on the toggle.
    if (this.hasStructuredTarget) {
      this.structuredTarget.hidden = isAdvanced
    }
    // Show / hide the raw cron textbox — inverse of structured. Without
    // this the advanced toggle hides the structured fields but the
    // textbox stays invisible (it starts with the `hidden` attribute),
    // and the user sees an empty fieldset.
    this.advancedTarget.hidden = !isAdvanced
  }

  composeCron() {
    const [hh, mm] = (this.timeTarget.value || "02:00").split(":")
    const minute = parseInt(mm, 10) || 0
    const hour   = parseInt(hh, 10) || 0

    if (this.frequencyTarget.value === "daily") {
      return `${minute} ${hour} * * *`
    }

    if (this.frequencyTarget.value === "weekly") {
      const days = this.weekdayTargets
        .filter(el => el.checked)
        .map(el => parseInt(el.value, 10))
        .sort((a, b) => a - b)
      if (days.length === 0) return ""  // no days selected → empty cron, validation will catch
      return `${minute} ${hour} * * ${days.join(",")}`
    }

    return ""
  }

  // Toggle handler — called by data-action on the checkbox.
  toggleAdvanced() {
    this.refresh()
  }

  // Helpers ----------------------------------------------------------

  setAdvanced(on) {
    this.advancedToggleTarget.checked = on
  }

  hhmm(hour, minute) {
    return `${String(parseInt(hour, 10)).padStart(2, "0")}:${String(parseInt(minute, 10)).padStart(2, "0")}`
  }
}
